import React, { FC, RefObject, useCallback, useMemo, useRef } from 'react'
import {
    ArticleTeaserProps,
    BasicTeaserProps,
    ComponentType,
    GalleryTeaserProps,
    StreamTeaserProps,
    TeaserLabelType,
    TeaserProps,
    VideoTeaserProps,
} from '@sport1/types/web'
import TeaserComponent from '@sport1/news-styleguide/Teaser'
import Types, { FontelloIconType } from '@sport1/news-styleguide/Types'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import { useRouter } from 'next/router'
import Image from 'next/image'
import {
    DESKTOP_MID_TEASER_HEIGHT,
    DESKTOP_MID_TEASER_WIDTH,
    LIST_TEASER_HEIGHT,
    LIST_TEASER_WIDTH,
    MOBILE_BIG_TEASER_HEIGHT,
    MOBILE_BIG_TEASER_WIDTH,
} from './const'
import { publishDateIsCurrentDate } from '@/helpers/dateHelper'
import { mapDeeplink } from '@/helpers/deeplink/mapper'
import { getArticleSlug, getGallerySlug, getVideoSlug } from '@/helpers/slugHelper'
import useInViewport from '@/hooks/useInViewport'
import { ComponentProps, TeaserSize, TeaserSizeTypes } from '@/types/teaser'
import { mapTrackingData, useTracking } from '@/utils/tracking/TrackingProvider'
import nofollowExternal from '@/utils/url/nofollowExternal'
import { takeLastTagWithKeys } from '@/helpers/tagsHelper'
import { useTopMediaDimensions } from '@/hooks/useTopMediaDimensions'
import { PLACEHOLDER_BLUR_BASE64, transformImageUrlToSize } from '@/helpers/imageHelper'
import { useMediaQueryContext } from '@/utils/breakpoints/MediaQuery'
import { SizeTypes } from '@/types/size'

type Props = {
    backgroundColor?: 'pearl' | 'onyx'
    title?: string
    size?: TeaserSize
    teaser: TeaserProps
    expand?: boolean
    isCardTeaser?: boolean
    headlineNumberOfLines?: number
    verticalExpand?: boolean
    hasBorder?: boolean
    isItTwoTeasersRightColumn?: boolean
}

export const LabelTypesMap: Map<
    TeaserLabelType | undefined,
    'live' | 'onyx' | 'breaking' | undefined
> = new Map([
    ['LIVE', 'live'],
    ['BREAKING_NEWS', 'breaking'],
    ['EXCLUSIVE', 'onyx'],
    ['UPDATE', 'onyx'],
    ['COLUMN', 'onyx'],
    ['COMMENT', 'onyx'],
    ['BRANDSTORY', 'onyx'],
    ['SPONSORED', 'onyx'],
])

export const LabelTextMap: Map<TeaserLabelType | undefined, string | undefined> = new Map([
    ['LIVE', 'live'],
    ['EXCLUSIVE', 'EXKLUSIV'],
    ['UPDATE', 'UPDATE'],
    ['COLUMN', 'KOLUMNE'],
    ['COMMENT', 'KOMMENTAR'],
    ['BRANDSTORY', 'BRAND STORY'],
    ['SPONSORED', 'SPONSORED'],
])

const Teaser: FC<Props> = ({
    backgroundColor,
    teaser,
    size = TeaserSizeTypes.BIG,
    expand,
    headlineNumberOfLines,
    hasBorder,
    isItTwoTeasersRightColumn,
}) => {
    const { currentDevice } = useMediaQueryContext()
    const { height } = useTopMediaDimensions()
    const width = Math.round(height * (3 / 2))
    const trackingData = mapTrackingData(teaser.meta?.tracking)
    const { trackTeaserImpression, trackTeaserClick } = useTracking()
    const ref = useRef<HTMLDivElement>(null)
    const isVisible = useInViewport(ref)
    const webTarget = teaser.target ? teaser.target : '_self'
    //margin added only for countdown, after em we should get rid of useRouter below and mocks in tests
    //to find mocks check this MR -> https://gitlab.sport1dev.de/ng/Outgest/Edge/sport1-website/-/merge_requests/1603/diffs
    const { asPath } = useRouter()
    React.useEffect(() => {
        if (isVisible) {
            trackTeaserImpression(trackingData)
        }
    }, [isVisible, trackingData, trackTeaserImpression])

    const getLabelProps = useCallback(
        (
            rawLabel: TeaserLabelType | undefined,
            transfer?: boolean
        ):
            | [Types.TeaserLabelProps]
            | [Types.TeaserLabelProps, Types.TeaserLabelProps]
            | undefined => {
            const type = transfer ? 'transfer' : LabelTypesMap.get(rawLabel)
            const text = LabelTextMap.get(rawLabel)
            if (type) {
                if (type === 'onyx') {
                    if (text) {
                        return [{ labelType: type, labelText: text }]
                    }
                } else {
                    return [{ labelType: type }]
                }
            }
        },
        []
    )

    const getBasicProps = useCallback(
        (
            data: BasicTeaserProps & Pick<Partial<ArticleTeaserProps>, 'transfer'>
        ): ComponentProps => {
            const { tags, publishedAt, title, label, description, transfer, summary, imageUrl } =
                data
            const tag = takeLastTagWithKeys(tags, ['iconUrl', 'title', 'iconUrlType'])
            return {
                tag: {
                    name: tag?.title || '',
                    imageUrl: tag?.iconUrl,
                    imageAlt: tag?.title,
                    iconUrlType: tag?.iconUrlType,
                    iconName: tag?.iconName as FontelloIconType,
                },
                mainImageUrl: imageUrl ?? '',
                mainImageAlt: description || title,
                published: publishedAt ? publishDateIsCurrentDate(publishedAt) : undefined,
                headlineText: title,
                type: 'ARTICLE',
                backgroundColor,
                textColor: backgroundColor === 'onyx' ? 'pearl' : 'onyx',
                onPress: () => trackTeaserClick(trackingData),
                labels: getLabelProps(label, transfer),
                abstract: summary,
                hasBorder,
            }
        },
        [backgroundColor, trackingData, getLabelProps, trackTeaserClick, hasBorder]
    )

    const getArticleProps = useCallback(
        (article: ArticleTeaserProps): ComponentProps => {
            return {
                ...getBasicProps(article),
                href: getArticleSlug(article),
                type: 'ARTICLE',
            }
        },
        [getBasicProps]
    )

    const getStreamTeaserProps = useCallback(
        (stream: StreamTeaserProps): ComponentProps => {
            return {
                ...getBasicProps(stream),
                type: 'ARTICLE',
                href: mapDeeplink(stream.url),
            }
        },
        [getBasicProps]
    )

    const getGalleryProps = useCallback(
        (gallery: GalleryTeaserProps): ComponentProps => {
            return {
                ...getBasicProps(gallery),
                href: getGallerySlug(gallery),
                type: 'GALLERY',
            }
        },
        [getBasicProps]
    )

    const getVideoProps = useCallback(
        (video: VideoTeaserProps): ComponentProps => {
            return {
                ...getBasicProps(video),
                videoDuration: video.duration,
                href: getVideoSlug(video, true),
                type: 'VIDEO',
            }
        },
        [getBasicProps]
    )

    const getComponentProps = useCallback((): ComponentProps => {
        switch (teaser.type) {
            case ComponentType.GALLERY_TEASER:
                return getGalleryProps(teaser as GalleryTeaserProps)
            case ComponentType.VIDEO_TEASER:
                return getVideoProps(teaser as VideoTeaserProps)
            case ComponentType.EDITORIAL_STREAM_TEASER:
                return getStreamTeaserProps(teaser as StreamTeaserProps)
            default:
                return getArticleProps(teaser as ArticleTeaserProps)
        }
    }, [getArticleProps, getGalleryProps, getVideoProps, getStreamTeaserProps, teaser])

    const teaserProps = getComponentProps()

    return useMemo(() => {
        switch (size) {
            case TeaserSizeTypes.HORIZONTAL_BIG:
                // extra container because ref is not defined in HorizontalBigTeaserComponent because of dynamic import
                return (
                    <NonFlexingContainer
                        innerRef={ref}
                        //margin added only for countdown, after em we should get rid of margintop below
                        marginTop={
                            asPath === '/channel/fussball/em'
                                ? ['spacing-6', 'spacing-6', 'spacing-6', 'spacing-11']
                                : 'spacing-none'
                        }
                    >
                        <TeaserComponent
                            variant={[
                                'HorizontalTeaserL',
                                'HorizontalTeaserL',
                                'HorizontalTeaserXL',
                                isItTwoTeasersRightColumn
                                    ? 'HorizontalTeaserL'
                                    : 'HorizontalTeaserXL',
                            ]}
                            testID={`${teaser.type}_horizontal_bigTeaser_${teaserProps.headlineText}`}
                            webRel={nofollowExternal}
                            webTarget={webTarget}
                            {...teaserProps}
                            nextImage={
                                <div
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        aspectRatio: String(3 / 2),
                                    }}
                                >
                                    <Image
                                        src={transformImageUrlToSize(
                                            teaserProps?.mainImageUrl,
                                            width,
                                            height
                                        )}
                                        alt={teaserProps?.mainImageAlt || ''}
                                        style={{ objectFit: 'cover' }}
                                        fill
                                        sizes="(max-width: 1041px) 100vw, 1041px"
                                        quality={75}
                                        placeholder="blur"
                                        blurDataURL={PLACEHOLDER_BLUR_BASE64}
                                    />
                                </div>
                            }
                        />
                    </NonFlexingContainer>
                )
            case TeaserSizeTypes.BIG:
                const bigTeaserImgWidth =
                    currentDevice === SizeTypes.MOBILE ? MOBILE_BIG_TEASER_WIDTH : width
                const bigTeaserImgHeight =
                    currentDevice === SizeTypes.MOBILE ? MOBILE_BIG_TEASER_HEIGHT : height
                return (
                    <NonFlexingContainer
                        innerRef={ref}
                        //margin added only for countdown, after em we should get rid of margintop below
                        marginTop={asPath === '/channel/fussball/em' ? 'spacing-6' : 'spacing-none'}
                    >
                        <TeaserComponent
                            variant={['VerticalTeaserM', 'VerticalTeaserM', 'VerticalTeaserL']}
                            testID={`${teaser.type}_bigTeaser_${teaserProps.headlineText}`}
                            {...teaserProps}
                            innerRef={ref as RefObject<HTMLDivElement>}
                            webTarget={webTarget}
                            teaserIcon={teaser.teaserIcon}
                            webRel={nofollowExternal}
                            nextImage={
                                <div
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        aspectRatio: String(3 / 2),
                                    }}
                                >
                                    <Image
                                        src={transformImageUrlToSize(
                                            teaserProps?.mainImageUrl,
                                            bigTeaserImgWidth,
                                            bigTeaserImgHeight
                                        )}
                                        alt={teaserProps?.mainImageAlt || ''}
                                        style={{ objectFit: 'cover' }}
                                        fill
                                        fetchPriority="high"
                                        priority
                                        sizes="(max-width: 1023px) 100vw, 75vw"
                                        quality={75}
                                        placeholder="blur"
                                        blurDataURL="data:image/png;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/2wBDAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/wAARCAAKAAoDAREAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAABAcK/8QAJRAAAQIEBAcAAAAAAAAAAAAAAQIRABIhYQMxQVEFFSJSkqHR/8QAGQEAAgMBAAAAAAAAAAAAAAAAAgQBAwUG/8QAJREBAAAFAQgDAAAAAAAAAAAAAQACESExQRIiQlFhkaHBcbHR/9oADAMBAAIRAxEAPwDPPg8des5DuBNqTUBnYFqOTvuWwiRKJMltVoGvM64rrkjoV6mbWDNaHP14hPOgKTmlgfesBvHHN3H1B7K3pLe/F+xNsBazmpR6lZqN7wzIGw20l85iuFTr7leR+wvV5vdiXL8v3H//2Q=="
                                    />
                                </div>
                            }
                        />
                    </NonFlexingContainer>
                )
            case TeaserSizeTypes.MID:
                const midTeaserImgWidth =
                    currentDevice === SizeTypes.TABLET || currentDevice === SizeTypes.MOBILE
                        ? width
                        : DESKTOP_MID_TEASER_WIDTH
                const midTeaserImgHeight =
                    currentDevice === SizeTypes.TABLET || currentDevice === SizeTypes.MOBILE
                        ? height
                        : DESKTOP_MID_TEASER_HEIGHT
                return (
                    <TeaserComponent
                        variant={!expand ? 'VerticalTeaserXS' : 'VerticalTeaserS'}
                        testID={`${teaser.type}_midTeaser_${teaserProps.headlineText}`}
                        {...teaserProps}
                        innerRef={ref as RefObject<HTMLDivElement>}
                        headlineNumberOfLines={headlineNumberOfLines || 2}
                        webRel={nofollowExternal}
                        webTarget={webTarget}
                        teaserIcon={teaser.teaserIcon}
                        noExpand={!expand}
                        nextImage={
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    aspectRatio: String(3 / 2),
                                }}
                            >
                                <Image
                                    src={transformImageUrlToSize(
                                        teaserProps?.mainImageUrl,
                                        midTeaserImgWidth,
                                        midTeaserImgHeight
                                    )}
                                    alt={teaserProps?.mainImageAlt || ''}
                                    style={{ objectFit: 'cover' }}
                                    fill
                                    sizes="(max-width: 1023px) 50vw, 33vw"
                                    quality={75}
                                    placeholder="blur"
                                    blurDataURL="data:image/png;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/2wBDAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/wAARCAAKAAoDAREAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAABAcK/8QAJRAAAQIEBAcAAAAAAAAAAAAAAQIRABIhYQMxQVEFFSJSkqHR/8QAGQEAAgMBAAAAAAAAAAAAAAAAAgQBAwUG/8QAJREBAAAFAQgDAAAAAAAAAAAAAQACESExQRIiQlFhkaHBcbHR/9oADAMBAAIRAxEAPwDPPg8des5DuBNqTUBnYFqOTvuWwiRKJMltVoGvM64rrkjoV6mbWDNaHP14hPOgKTmlgfesBvHHN3H1B7K3pLe/F+xNsBazmpR6lZqN7wzIGw20l85iuFTr7leR+wvV5vdiXL8v3H//2Q=="
                                />
                            </div>
                        }
                    />
                )
            case TeaserSizeTypes.SMALL:
                return (
                    <TeaserComponent
                        variant="HorizontalTeaserS"
                        testID={`${teaser.type}_smallTeaser_${teaserProps.headlineText}`}
                        {...teaserProps}
                        innerRef={ref as RefObject<HTMLDivElement>}
                        webRel={nofollowExternal}
                        webTarget={webTarget}
                        teaserIcon={teaser.teaserIcon}
                        nextImage={
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    aspectRatio: String(3 / 2),
                                }}
                            >
                                <Image
                                    src={transformImageUrlToSize(
                                        teaserProps?.mainImageUrl,
                                        width,
                                        height
                                    )}
                                    alt={teaserProps?.mainImageAlt || ''}
                                    style={{ objectFit: 'cover' }}
                                    fill
                                    sizes="(max-width: 767px) 40vw, (max-width: 1023px) 50vw, 33vw"
                                    quality={75}
                                    placeholder="blur"
                                    blurDataURL="data:image/png;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/2wBDAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/wAARCAAKAAoDAREAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAABAcK/8QAJRAAAQIEBAcAAAAAAAAAAAAAAQIRABIhYQMxQVEFFSJSkqHR/8QAGQEAAgMBAAAAAAAAAAAAAAAAAgQBAwUG/8QAJREBAAAFAQgDAAAAAAAAAAAAAQACESExQRIiQlFhkaHBcbHR/9oADAMBAAIRAxEAPwDPPg8des5DuBNqTUBnYFqOTvuWwiRKJMltVoGvM64rrkjoV6mbWDNaHP14hPOgKTmlgfesBvHHN3H1B7K3pLe/F+xNsBazmpR6lZqN7wzIGw20l85iuFTr7leR+wvV5vdiXL8v3H//2Q=="
                                />
                            </div>
                        }
                    />
                )
            case TeaserSizeTypes.LIST:
                return (
                    <TeaserComponent
                        variant="HorizontalTeaserM"
                        testID={`${teaser.type}_listTeaser_${teaserProps.headlineText}`}
                        {...teaserProps}
                        innerRef={ref as RefObject<HTMLDivElement>}
                        webTarget={webTarget}
                        webRel={nofollowExternal}
                        nextImage={
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    aspectRatio: String(3 / 2),
                                }}
                            >
                                <Image
                                    src={transformImageUrlToSize(
                                        teaserProps?.mainImageUrl,
                                        LIST_TEASER_WIDTH,
                                        LIST_TEASER_HEIGHT
                                    )}
                                    alt={teaserProps?.mainImageAlt || ''}
                                    style={{ objectFit: 'cover' }}
                                    fill
                                    sizes="50vw"
                                    quality={75}
                                    placeholder="blur"
                                    blurDataURL="data:image/png;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/2wBDAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/wAARCAAKAAoDAREAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAABAcK/8QAJRAAAQIEBAcAAAAAAAAAAAAAAQIRABIhYQMxQVEFFSJSkqHR/8QAGQEAAgMBAAAAAAAAAAAAAAAAAgQBAwUG/8QAJREBAAAFAQgDAAAAAAAAAAAAAQACESExQRIiQlFhkaHBcbHR/9oADAMBAAIRAxEAPwDPPg8des5DuBNqTUBnYFqOTvuWwiRKJMltVoGvM64rrkjoV6mbWDNaHP14hPOgKTmlgfesBvHHN3H1B7K3pLe/F+xNsBazmpR6lZqN7wzIGw20l85iuFTr7leR+wvV5vdiXL8v3H//2Q=="
                                />
                            </div>
                        }
                    />
                )
        }
    }, [
        size,
        teaserProps,
        expand,
        headlineNumberOfLines,
        teaser,
        webTarget,
        asPath,
        isItTwoTeasersRightColumn,
        height,
        width,
        currentDevice,
    ])
}

export default Teaser
